import {ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  BaseComponent,
  GroupedIssueActivityLog,
  IdKeyAndHeader,
  CrmIssueActivityLog,
  IssueChangeType,
  FileService
} from "compob2b-libs";
import {AdminSharedModule} from "../../../../../admin-shared/admin-shared.module";
import {DatePipe, NgForOf} from "@angular/common";
import {NbOptionModule, NbSelectModule, NbTooltipModule} from "@nebular/theme";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {TuiAvatar} from "@taiga-ui/kit";
import {TuiInitialsPipe} from "@taiga-ui/core";
import {TuiInputDateModule, TuiTextfieldControllerModule} from "@taiga-ui/legacy";
import moment from "moment/moment";
import {takeUntil} from "rxjs/operators";

@Component({
    selector: 'app-issue-activity-logs',
    imports: [
        AdminSharedModule,
        DatePipe,
        NbOptionModule,
        NbSelectModule,
        NgForOf,
        ReactiveFormsModule,
        TuiAvatar,
        TuiInitialsPipe,
        TuiInputDateModule,
        TuiTextfieldControllerModule,
        NbTooltipModule
    ],
    standalone: true,
    templateUrl: './issue-activity-logs.component.html',
    styleUrl: './issue-activity-logs.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IssueActivityLogsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input()
  allLogs: GroupedIssueActivityLog[] = [];
  filteredLogs: GroupedIssueActivityLog[] = [];

  logTypes: IdKeyAndHeader[] = [
    {id: 'CREATED', header: 'Создание задачи'},
    {id: 'HEADER_CHANGED', header: ' Переименование задачи'},
    {id: 'COLUMN_CHANGED', header: 'Перемещение задачи'},
    {id: 'ASSIGNEE_ASSIGNED', header: 'Назначение исполнителя'},
    {id: 'ASSIGNEE_CHANGED', header: 'Изменение исполнителя'},
    {id: 'FILE_ATTACHED', header: 'Прикрепление файла'},
    {id: 'DEADLINE_SET', header: 'Назначение дедлайна'},
    {id: 'DEADLINE_CHANGED', header: 'Изменение дедлайна'},
    {id: 'ARCHIVED', header: 'Архивирование задачи'},
    {id: 'UNARCHIVED', header: 'Разархивирование задачи'},
  ];

  logFilterForm: FormGroup = new FormGroup({
    'date': new FormControl(null),
    'type': new FormControl(null),
  });

  constructor(private readonly datePipe: DatePipe,
              private readonly fileService: FileService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('allLogs')) {
      this.filteredLogs = this.filterLogs(this.allLogs);
    }
  }

  ngOnInit(): void {
    this.filteredLogs = this.filterLogs(this.allLogs);

    this.logFilterForm.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.filteredLogs = this.filterLogs(this.allLogs);
      });
  }

  private filterLogs(logs: GroupedIssueActivityLog[]): GroupedIssueActivityLog[] {
    const typeFilter: IssueChangeType = this.logFilterForm.get('type').value;
    let filteredLogs: GroupedIssueActivityLog[] = JSON.parse(JSON.stringify(logs));
    if (typeFilter) {
      filteredLogs = filteredLogs
        .map(dayLogs => {
          dayLogs.logs = dayLogs.logs.filter(log => log.changeType === typeFilter);
          return dayLogs;
        })
        .filter(dayLogs => dayLogs.logs?.length > 0);
    }

    const dateFilter: Date = this.logFilterForm.get('date').value;
    if (dateFilter) {
      filteredLogs = filteredLogs.filter(dayLogs => moment(dayLogs.createdAt).isSame(dateFilter));
    }

    return filteredLogs;
  }

  getPictureSrc(picture: string) {
    return this.fileService.getImageSrc(picture, 96);
  }

  getChangeContent(log: CrmIssueActivityLog): string {
    switch (log.changeType) {
      case IssueChangeType.CREATED:
        return 'Задача добавлена'
      case IssueChangeType.HEADER_CHANGED:
        return `Задача переименована с "${log.change.header.from}" на "${log.change.header.to}"`
      case IssueChangeType.COLUMN_CHANGED:
        return `${log.change.column.from?.header ? `Задача перемещена с "${log.change.column.from.header}"` : 'Статус задачи изменен'} на "${log.change.column.to.header}"`
      case IssueChangeType.ASSIGNEE_ASSIGNED:
        return `Назначен исполнитель "${log.change.assignee.to.lastName} ${log.change.assignee.to.firstName}"`
      case IssueChangeType.ASSIGNEE_CHANGED:
        const from = log.change.assignee?.from;
        const to = log.change.assignee?.to;

        if (!to || (!to.firstName && !to.lastName)) {
          return from && (from.firstName || from.lastName)
              ? `Исполнитель "${from.lastName} ${from.firstName}" удален`
              : `Исполнитель удален`;
        }

        if (!from || (!from.firstName && !from.lastName)) {
          return `Назначен исполнитель "${to.lastName} ${to.firstName}"`;
        }

        return `Назначен изменен с "${from.lastName} ${from.firstName}" на "${to.lastName} ${to.firstName}"`;
      case IssueChangeType.DEADLINE_SET:
        return `Назначен дедлайн на ${this.datePipe.transform(log.change.deadline.to, 'dd.MM.yyyy')}`;
      case IssueChangeType.DEADLINE_CHANGED:
        return `Дедлайн изменен с ${this.datePipe.transform(log.change.deadline.from, 'dd.MM.yyyy')} на ${this.datePipe.transform(log.change.deadline.to, 'dd.MM.yyyy')}`
      case IssueChangeType.ARCHIVED:
        return 'Задача заархивирована'
      case IssueChangeType.UNARCHIVED:
        return 'Задача разархивирована'
      case IssueChangeType.FILE_ATTACHED:
        return `Добавлен файл ${log.change.document.fileName}`
      default:
        return '';
    }
  }
}
